import unidecode from 'unidecode';

/**
 * Ordered Unicode → ASCII replacement table applied before the `unidecode`
 * fallback. Order matters: multi-character source sequences must appear before
 * the single-character forms they contain, otherwise the single-char rule would
 * fire first and corrupt the multi-char replacement (e.g. `μg` must map to
 * `mcg` before `μ` maps to `mikro`, else we'd get `mikrog`).
 *
 * To add a new symbol, add a `[from, to]` row in the correct position — no
 * other code change is required.
 */
const UNICODE_TO_ASCII_REPLACEMENTS: ReadonlyArray<readonly [string, string]> =
  [
    // Multi-char sequences FIRST (before single chars to avoid double-replacement)
    ['μg', 'mcg'],
    ['µg', 'mcg'],

    // Temperature - Indonesian
    ['°C', ' derajat C'],
    ['°', ' derajat'],

    // Vertical (trend) arrows - Indonesian clinical terms
    ['↑', 'meningkat'],
    ['↓', 'menurun'],

    // Directional arrows - preserve direction as ASCII arrows rather than letting
    // unidecode collapse them to a meaning-less bare hyphen.
    ['⇔', '<=>'],
    ['⇒', '=>'],
    ['⇐', '<='],
    ['↔', '<->'],
    ['→', '->'],
    ['←', '<-'],

    // Beta
    ['β-blocker', 'beta-blocker'],
    ['β', 'beta'],

    // Plus-minus
    ['±', '+/-'],

    // Comparison / relation operators
    ['≥', '>='],
    ['≤', '<='],
    ['≈', '~'],
    ['≠', '!='],
    ['∝', 'proporsional'],

    // Single micro (after μg/µg handled)
    ['μ', 'mikro'],
    ['µ', 'mikro'],
  ];

/**
 * Convert Indonesian medical text with Unicode characters to ASCII-safe equivalents.
 *
 * This function handles the Siloam EMR's limitation of only supporting ASCII characters
 * by converting unicode symbols to their ASCII equivalents. It applies the ordered
 * {@link UNICODE_TO_ASCII_REPLACEMENTS} table for Indonesian medical terminology and
 * common symbols, then uses the unidecode library for any remaining unicode characters.
 *
 * @param text - The text containing unicode symbols
 * @returns Text with ASCII-safe replacements
 */
export function convertIndonesianUnicodeToAscii(text: string): string {
  if (!text) return text;

  let cleaned = text;
  for (const [from, to] of UNICODE_TO_ASCII_REPLACEMENTS) {
    cleaned = cleaned.replaceAll(from, to);
  }

  // Let unidecode handle the rest:
  // - Chemical formulas (O₂, CO₂)
  // - Fractions (½, ¼, ¾)
  // - Superscripts (², ³)
  // - Math (×)
  // - Dashes (–, —)
  // - Chinese → pinyin (生, 肉)
  return unidecode(cleaned);
}
